import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { CommonBookingFeatureState, SeatMapActions } from '@fcom/common-booking/index';
import { finShare, snapshot } from '@fcom/rx/index';
import { FinnairBoundItem, FinnairOrder } from '@fcom/dapi/api/models';
import { ButtonTheme, IconPosition, ModalButtons, NotificationTheme, StepperMode } from '@fcom/ui-components/index';
import { BoundType } from '@fcom/dapi/index';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { ConfigService, NativeBridgeService, RootPaths } from '@fcom/core/index';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate/index';
import { isNavigationEvent } from '@fcom/common/utils';
import { ScrollService, ToasterService } from '@fcom/common/services';
import { ModalService } from '@fcom/ui-components/components/modal/service/modal.service';
import { shouldShowKidsInBusinessNotification } from '@fcom/common-booking/utils/common-booking.utils';
import { CheckInService } from '@fcom/manage-booking-common/services/check-in.service';

import { ManageBookingFeatureState } from '../../../interfaces';
import { CheckInStep, MmbStage } from '../../../enums';
import { isServicesDirty } from '../../../store/selectors/state.selector';
import {
  getCheckInStepForPath,
  getSeatsFromOrder,
  isCarrierConnectBound,
  isCarrierConnectBoundSeatsEnabled,
  isSeatsReassignmentSuccessful,
} from '../../../utils/check-in.utils';
import { FinnairPassengerItemWithApis } from '../interface/check-in.interface';
import { ExitRowSeatInfo, ExitRowSeatsConfirmation } from '../../../interfaces/checkin.interface';
import { ManageBookingService } from '../../../services';
import { isSeatsEligibilityDisabled } from '../../../utils';

interface CheckInStepWithOrder {
  name: CheckInStep;
  sequence: number;
}

const STEP_KEY = 'MMB.checkIn.funnel.stepName';

@Component({
  selector: 'fin-check-in-funnel-routing',
  templateUrl: 'check-in-funnel-routing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInFunnelRoutingComponent implements OnInit, OnDestroy {
  private feedbackService = inject(FeedbackService);
  private store$ = inject<Store<CommonBookingFeatureState & ManageBookingFeatureState>>(Store);
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private checkInService = inject(CheckInService);
  private scrollService = inject(ScrollService);
  private manageBookingService = inject(ManageBookingService);
  private toasterService = inject(ToasterService);
  private nativeBridgeService = inject(NativeBridgeService);
  private modalService = inject(ModalService);
  private configService = inject(ConfigService);

  readonly MmbStage = MmbStage;
  readonly ModalButtons = ModalButtons;
  readonly BoundType = BoundType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly StepperMode = StepperMode;
  readonly CheckInStep = CheckInStep;

  order$: Observable<FinnairOrder>;
  bound$: Observable<FinnairBoundItem>;
  passengers$: Observable<FinnairPassengerItemWithApis[]>;
  isAPISCompleted$: Observable<boolean>;
  locations$: Observable<FinnairOrder['locations']>;
  viewFlightDetailsOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pageTitle$: Observable<string>;
  pagePath$: Observable<string>;
  showNavigateBackButton$: Observable<boolean>;
  currentStep$: Observable<CheckInStep>;
  currentStepNumber$: Observable<number>;
  closeSeatChangesModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  stepperSteps$: Observable<Array<{ ariaLabel: string }>>;
  checkInStepsOrder$: Observable<CheckInStepWithOrder[]>;
  isAPISRequired$: Observable<boolean>;
  isEligibleForVoluntaryDeniedBoarding$: Observable<boolean>;
  isModifyCheckIn$: Observable<boolean>;

  exitRowSeatsToConfirm$: Observable<ExitRowSeatInfo[]>;
  showExitRowSeatsNotification$: Observable<boolean>;
  reassigningSeatsModalLoader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  enableChildSafetyNotice: boolean;
  isInsideApp: boolean;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.enableChildSafetyNotice = this.configService.cfg.enableKidsInBusinessNotification;
    this.isInsideApp = this.nativeBridgeService.isInsideNativeWebView;
    if (this.isInsideApp) {
      this.modalService.setHideCloseButton(true);
    }

    this.feedbackService.setSurvey(QualtricsSurveys.CHECK_IN);
    this.feedbackService.setFeedbackWidgetVisibility(true);

    this.subscriptions.add(
      this.router.events.pipe(filter(isNavigationEvent)).subscribe(() => {
        this.scrollService.scrollTop();
      })
    );

    this.pagePath$ = this.router.events.pipe(
      filter<NavigationEnd>(isNavigationEvent),
      map((navigation) => navigation.url),
      startWith(this.router.url),
      distinctUntilChanged()
    );

    this.currentStep$ = this.pagePath$.pipe(
      map((path) => {
        return getCheckInStepForPath(path);
      }),
      distinctUntilChanged(),
      finShare()
    );
    this.showNavigateBackButton$ = this.currentStep$.pipe(
      map(
        (step) =>
          step !== CheckInStep.PASSENGERS &&
          step !== CheckInStep.CONFIRMATION &&
          step !== CheckInStep.VOLUNTARY_DENIED_BOARDING
      ),
      finShare()
    );

    this.order$ = this.checkInService.getOrder();
    this.bound$ = this.checkInService.getBound();
    this.passengers$ = this.checkInService.getPassengersWithApis();
    this.isAPISRequired$ = this.checkInService.isAPISRequired();
    this.isModifyCheckIn$ = this.checkInService.isModifyCheckIn();
    this.isEligibleForVoluntaryDeniedBoarding$ = this.checkInService.isEligibleForVoluntaryDeniedBoarding();
    this.isAPISCompleted$ = this.checkInService.isApisCompleted();
    this.pageTitle$ = combineLatest([this.currentStep$, this.isAPISRequired$, this.isAPISCompleted$]).pipe(
      map(([step, isApisRequired, isAPISCompleted]) =>
        isApisRequired ? this.getTranslationKeyForStep(step, isAPISCompleted) : this.getTranslationKeyForStep(step)
      )
    );
    this.locations$ = this.order$.pipe(map((order) => order.locations));
    const steps$ = combineLatest([
      this.isAPISRequired$,
      this.order$,
      this.bound$,
      this.passengers$,
      this.isEligibleForVoluntaryDeniedBoarding$,
      this.isModifyCheckIn$,
    ]).pipe(
      map(([isAPISRequired, order, bound, passengers, isEligibleForVoluntaryDeniedBoarding, isModifyCheckIn]) => {
        return [
          CheckInStep.PASSENGERS,
          this.enableChildSafetyNotice && shouldShowKidsInBusinessNotification([bound], passengers)
            ? CheckInStep.CHILD_SAFETY_NOTICE
            : undefined,
          isAPISRequired ? CheckInStep.APIS : undefined,
          (isCarrierConnectBound(order, bound) && !isCarrierConnectBoundSeatsEnabled(order, bound)) ||
          isSeatsEligibilityDisabled(order, bound.id)
            ? undefined
            : CheckInStep.SEATMAP,
          CheckInStep.KYT,
          CheckInStep.RULES,
          isEligibleForVoluntaryDeniedBoarding && !isModifyCheckIn ? CheckInStep.VOLUNTARY_DENIED_BOARDING : undefined,
        ].filter(Boolean);
      }),
      finShare()
    );

    this.stepperSteps$ = steps$.pipe(map((steps) => steps.map(() => ({ ariaLabel: STEP_KEY }))));
    this.checkInStepsOrder$ = steps$.pipe(
      map((steps) => steps.map((step, index) => ({ name: step, sequence: index })))
    );

    this.currentStepNumber$ = combineLatest([this.currentStep$, this.checkInStepsOrder$]).pipe(
      map(
        ([currentStepName, checkInStepsOrder]) =>
          checkInStepsOrder.find((step) => step.name === currentStepName)?.sequence
      ),
      finShare()
    );

    this.showExitRowSeatsNotification$ = this.checkInService.getShowExitRowSeatsNotification();
    this.exitRowSeatsToConfirm$ = this.checkInService.getExitRowSeatsToConfirm();

    this.subscriptions.add(
      this.checkInService
        .getGoToNextCheckInStepTrigger()
        .pipe(
          withLatestFrom(this.currentStepNumber$, this.checkInStepsOrder$),
          map(([, currentStepNumber, checkInStepsOrder]) => {
            return this.isLastStep(currentStepNumber, checkInStepsOrder)
              ? CheckInStep.CONFIRMATION
              : (checkInStepsOrder.find((step) => step.sequence === currentStepNumber + 1) ?? checkInStepsOrder[0])
                  .name;
          })
        )
        .subscribe((nextStep) => {
          this.goToNextCheckInStep(nextStep);
        })
    );
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
    this.subscriptions.unsubscribe();
  }

  exitCheckIn(): void {
    this.gtmService.trackElement(
      'check-in-flow_exit-flow',
      GaContext.CHECK_IN,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }

  navigateBack(): void {
    this.subscriptions.add(
      combineLatest([this.currentStepNumber$, this.currentStep$, this.checkInStepsOrder$])
        .pipe(take(1))
        .subscribe(([currentStepNumber, currentStep, checkInStepsOrder]) => {
          const previousStep = checkInStepsOrder.find((step) => step.sequence === currentStepNumber - 1);
          if (previousStep) {
            if (currentStep === CheckInStep.SEATMAP) {
              this.showSeatChangesModalOrNavigateBack();
            } else {
              this.redirectToPreviousStep(previousStep);
            }
          }
        })
    );
  }

  showSeatChangesModalOrNavigateBack(modalOpen = false): void {
    if (!modalOpen && snapshot(this.store$.pipe(isServicesDirty()))) {
      this.closeSeatChangesModalOpen$.next(true);
    } else {
      this.subscriptions.add(
        combineLatest([this.order$, this.currentStepNumber$, this.checkInStepsOrder$])
          .pipe(take(1))
          .subscribe(([order, stepNumber, checkInStepsOrder]) => {
            this.store$.dispatch(SeatMapActions.resetSeatSelections({ cartOrOrder: order }));
            this.redirectToPreviousStep(checkInStepsOrder.find((step) => step.sequence === stepNumber - 1));
          })
      );
    }
  }

  private getTranslationKeyForStep(step: string, isAPISCompleted = false) {
    switch (step) {
      case CheckInStep.PASSENGERS: {
        return 'MMB.checkIn.funnel.steps.passengers.title';
      }
      case CheckInStep.CHILD_SAFETY_NOTICE: {
        return 'MMB.checkIn.funnel.steps.childSafetyNotice.title';
      }
      case CheckInStep.APIS: {
        return isAPISCompleted
          ? 'MMB.checkIn.steps.apis.detailsCompleteTitle'
          : 'MMB.checkIn.steps.apis.detailsMissingTitle';
      }
      case CheckInStep.SEATMAP: {
        return 'MMB.checkIn.funnel.steps.seats.title';
      }
      case CheckInStep.KYT: {
        return 'MMB.checkIn.kyt.title';
      }
      case CheckInStep.VOLUNTARY_DENIED_BOARDING: {
        return 'MMB.checkIn.funnel.steps.voluntaryDeniedBoarding.title';
      }
    }
    return 'MMB.checkIn.funnel.steps.restrictedItems.title';
  }

  private redirectToPreviousStep(previousStep: CheckInStepWithOrder): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/check-in/step/${previousStep.name}`
    );
  }

  onConfirmExitRowSeats(exitRowSeatsConfirmation: ExitRowSeatsConfirmation): void {
    const request$ = this.exitRowSeatsToConfirm$.pipe(
      take(1),
      map((seats) => {
        return {
          flightsAndTravelers: seats
            .map((flightWithSeats) => {
              return {
                fragmentId: flightWithSeats.fragmentId,
                paxIds: flightWithSeats.passengers
                  .filter((pax) => !exitRowSeatsConfirmation.confirmedPax.includes(pax.id))
                  .map((pax) => pax.id),
              };
            })
            .filter((fragmentIdWithPaxIds) => fragmentIdWithPaxIds.paxIds?.length > 0)
            .reduce<Record<string, string[]>>((result, currentExitRowInfo) => {
              result = {
                ...result,
                [currentExitRowInfo.fragmentId]: currentExitRowInfo.paxIds,
              };
              return result;
            }, {}),
        };
      })
    );
    const nonEmptyRequest$ = request$.pipe(filter((request) => Object.keys(request.flightsAndTravelers)?.length > 0));
    const reassignExitRowSeatsCall$ = nonEmptyRequest$.pipe(
      tap(() => this.reassigningSeatsModalLoader$.next(true)),
      switchMap((request) => this.manageBookingService.reassignExitRowSeats(request))
    );
    const goToNextStep$ = request$.pipe(filter((request) => Object.keys(request.flightsAndTravelers)?.length === 0));

    this.checkInService.setConfirmedExitRowSeatPassengers(exitRowSeatsConfirmation.confirmedPax);
    this.checkInService.setShowExitRowSeatsNotification(false);

    this.subscriptions.add(
      goToNextStep$.subscribe(() => {
        this.checkInService.triggerNextCheckInStep();
      })
    );

    this.subscriptions.add(
      combineLatest([
        reassignExitRowSeatsCall$,
        nonEmptyRequest$,
        this.bound$.pipe(
          take(1),
          map((bound) => bound.id)
        ),
      ])
        .pipe(tap(() => this.reassigningSeatsModalLoader$.next(false)))
        .subscribe({
          next: ([order, request, boundId]) => {
            if (isSeatsReassignmentSuccessful(getSeatsFromOrder(order, boundId), request)) {
              this.checkInService.setShowSeatsReassignmentSuccessfulMessage(true);
            } else {
              this.checkInService.setShowNoSeatsLeftNotificationMessage$(true);
            }
            this.checkInService.triggerNextCheckInStep();
          },
          error: () => {
            this.toasterService.addMessageToQueue({
              id: 'checkIn',
              theme: NotificationTheme.ALERT,
              keepMessageAfterNavigation: true,
              contentKey: 'MMB.checkIn.notifications.error.seatsReassignFailed',
            });
          },
        })
    );
  }

  private goToNextCheckInStep(nextStep: CheckInStep) {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/check-in/step/${nextStep}`
    );
  }

  private isLastStep(currentStepNumber: number, checkInStepsOrder: CheckInStepWithOrder[]): boolean {
    return checkInStepsOrder.length - 1 === currentStepNumber;
  }
}
